import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, TextField, Alert, CircularProgress } from '@mui/material';
import { PillButton } from '../../superAdminUi';
import ModalShell from './ModalShell';
import { changeUserEmail } from '../../../services/api/identityUserService';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Change Email — the change is not applied immediately: a confirmation link is sent to the NEW
// address and the email switches only once that address confirms (48h). A duplicate address comes
// back as a 409 (status carried on the thrown error) and is shown as a clear "already in use" state.
function ChangeEmailModal({ open, onClose, user, onCompleted }) {
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState('form');
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [ssoWarning, setSsoWarning] = useState('');

  const reset = () => {
    setEmail('');
    setPhase('form');
    setError('');
    setSentTo('');
    setSsoWarning('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    const next = email.trim();
    if (!EMAIL_PATTERN.test(next)) {
      setError('Please enter a valid email address.');
      return;
    }
    setPhase('submitting');
    setError('');
    try {
      const result = await changeUserEmail(user.id, next);
      setSentTo(result?.sentTo || next);
      setSsoWarning(result?.ssoWarning || '');
      setPhase('sent');
      if (onCompleted) onCompleted();
    } catch (err) {
      if (err?.status === 409) {
        setError('That email address is already in use by another account.');
      } else if (err?.status === 400) {
        setError(err?.message || 'That email address cannot be used. It may be the same as the current one.');
      } else {
        setError(err?.message || 'The change could not be requested. Please try again.');
      }
      setPhase('form');
    }
  };

  return (
    <ModalShell open={open} onClose={handleClose} title='Change Email'>
      {phase === 'sent' ? (
        <Box>
          <Alert severity='success' sx={{ mb: 2 }}>
            A confirmation link has been sent to {sentTo}. The address will change once they confirm it
            (the link is valid for 48 hours).
          </Alert>
          {ssoWarning && (
            <Alert severity='warning' sx={{ mb: 2 }}>
              {ssoWarning}
            </Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <PillButton type='button' $variant='teal' $size='lg' onClick={handleClose}>
              Done
            </PillButton>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='body2' color='text.secondary'>
            Current email: {user?.email || '—'}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            A confirmation link will be sent to the new address; the email changes only once it is confirmed.
          </Typography>
          {error && <Alert severity='error'>{error}</Alert>}
          <TextField
            label='New email'
            type='email'
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            fullWidth
            required
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <PillButton
              type='button'
              $variant='outline'
              $size='lg'
              onClick={handleClose}
              disabled={phase === 'submitting'}
            >
              Cancel
            </PillButton>
            <PillButton
              type='button'
              $variant='teal'
              $size='lg'
              onClick={handleSubmit}
              disabled={phase === 'submitting'}
            >
              {phase === 'submitting' ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Send confirmation'}
            </PillButton>
          </Box>
        </Box>
      )}
    </ModalShell>
  );
}

ChangeEmailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    email: PropTypes.string,
  }).isRequired,
  onCompleted: PropTypes.func,
};

export default ChangeEmailModal;
